// §6.1 / §14 — mounting a skin.
//
// Changing the skin swaps the module, rewrites `data-skin`, replaces the token stylesheet and
// re-renders every frame. **It never touches `.win-host`** (§5.7) — the host layer does not
// subscribe to the skin at all, which is what makes a skin change cost zero iframe reloads.
import {
  createContext,
  useContext,
  useEffect,
  useInsertionEffect,
  useMemo,
  type ReactNode,
} from 'react';
import type { UiSkin } from '@/skins/contract';
import { useKernel, useSetting, useStoreValue } from '@/kernel/react';
import { resolveSkin } from '@/skins/registry';

const SkinContext = createContext<UiSkin | null>(null);

export function useSkin(): UiSkin {
  const skin = useContext(SkinContext);
  if (!skin) throw new Error('useSkin: no <SkinProvider> above this component');
  return skin;
}

const TOKENS_ELEMENT_ID = 'hangar-skin-tokens';

export function SkinProvider({ children }: { children: ReactNode }) {
  const kernel = useKernel();
  const skinId = useSetting('skin');
  const themePreference = useSetting('theme');
  const accent = useStoreValue(kernel.settings.store, (s) => s.accent);

  const { skin, fallbackReason } = useMemo(() => resolveSkin(skinId), [skinId]);

  // F13 — a skin that fails validation falls back to house and says so, rather than rendering
  // a chromeless desktop.
  useEffect(() => {
    if (fallbackReason) kernel.shellui.showToast(fallbackReason);
  }, [fallbackReason, kernel]);

  // An **insertion** effect, not an ordinary one (D-099). Effects run child-before-parent, and
  // the desktop below this provider measures `--bar-height` to compute the work area (§5.4) — so
  // an ordinary effect here would publish the new skin's tokens *after* the child had already
  // measured the old skin's, and every window on the Windows skin would maximize to the house
  // skin's 40px taskbar. Insertion effects are React's answer to exactly this: they run for the
  // whole commit before any layout or passive effect, and injecting a stylesheet is what they
  // are for.
  useInsertionEffect(() => {
    const root = document.documentElement;
    root.dataset['skin'] = skin.id;

    let style = document.getElementById(TOKENS_ELEMENT_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = TOKENS_ELEMENT_ID;
      document.head.appendChild(style);
    }
    style.textContent = skin.tokens;
  }, [skin]);

  useEffect(() => {
    const root = document.documentElement;
    const apply = (): void => {
      const theme = kernel.resolvedTheme();
      root.dataset['theme'] = theme;
      // §9.3 shell→app `window/themeChanged` — announced from the one place that recomputes the
      // *resolved* theme, because a `system` preference flips with the OS and no setting changes
      // at all. Only on a real change, so a skin swap does not re-announce what apps already
      // have (D-130).
      if (root.dataset['announcedTheme'] === theme) return;
      root.dataset['announcedTheme'] = theme;
      kernel.bus.emit('settings:themeChanged', { theme });
    };
    apply();
    if (themePreference !== 'system' || typeof matchMedia !== 'function') return;
    const media = matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [kernel, themePreference]);

  useEffect(() => {
    const root = document.documentElement;
    if (accent) root.style.setProperty('--accent', accent);
    else root.style.removeProperty('--accent');
  }, [accent]);

  useEffect(() => {
    kernel.sound.setRecipes(skin.sounds);
  }, [kernel, skin]);

  return <SkinContext.Provider value={skin}>{children}</SkinContext.Provider>;
}
